import React, { useState } from 'react';
import { parseIsoDate, toIso, startOfMonth, shiftMonths, isSameDay, buildGrid } from './dateUtils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface LogsCalendarPickerProps {
  /** Currently picked date, ISO `YYYY-MM-DD`, or empty/undefined if none yet. */
  value?: string;
  /** Fires with the newly picked date, ISO `YYYY-MM-DD`. */
  onSelect: (isoDate: string) => void;
  /** "Clear" footer link. */
  onClear: () => void;
  /** Inclusive lower bound, ISO `YYYY-MM-DD` — earlier days render disabled. */
  min?: string;
  /** Inclusive upper bound, ISO `YYYY-MM-DD` — later days render disabled. */
  max?: string;
  /** Accessible name for the popup (the field it belongs to — "From date"/"To date"). */
  ariaLabel: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Custom calendar popup for the Logs page's custom date-range row (Figma
 * node 2771-9999, "modal/small") — replaces the browser's native
 * `<input type="date">` picker (inconsistent across browsers/OSes, and
 * doesn't match the app's visual language) with an in-house month grid.
 *
 * Deliberately stateless about its own open/closed state and about
 * min/max validation beyond disabling out-of-range days — the parent
 * (`LogsFilterBar`) owns which popup (From/To) is open, closes it on
 * pick/Clear/outside-click, and is the source of truth for `value`.
 */
const LogsCalendarPicker: React.FC<LogsCalendarPickerProps> = ({
  value,
  onSelect,
  onClear,
  min,
  max,
  ariaLabel,
}) => {
  const selected = parseIsoDate(value);
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(startOfMonth(selected ?? today));

  const minDate = parseIsoDate(min);
  const maxDate = parseIsoDate(max);
  const isDisabled = (d: Date) => Boolean((minDate && d < minDate) || (maxDate && d > maxDate));

  const days = buildGrid(viewMonth);
  const monthLabel = viewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const handleDayClick = (d: Date) => {
    if (isDisabled(d)) return;
    if (d.getMonth() !== viewMonth.getMonth()) setViewMonth(startOfMonth(d));
    onSelect(toIso(d));
  };

  const handleToday = () => {
    setViewMonth(startOfMonth(today));
    if (!isDisabled(today)) onSelect(toIso(today));
  };

  return (
    <div className="lf-calendar-popup" role="dialog" aria-label={ariaLabel}>
      <div className="lf-calendar-header">
        <button
          type="button"
          className="lf-calendar-nav"
          aria-label="Previous month"
          onClick={() => setViewMonth((m) => shiftMonths(m, -1))}
        >
          <svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden>
            <path d="M7 1L1 7L7 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="lf-calendar-month-label">{monthLabel}</span>
        <button
          type="button"
          className="lf-calendar-nav"
          aria-label="Next month"
          onClick={() => setViewMonth((m) => shiftMonths(m, 1))}
        >
          <svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden>
            <path d="M1 1L7 7L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="lf-calendar-weekdays">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="lf-calendar-weekday">
            {label}
          </span>
        ))}
      </div>

      <div className="lf-calendar-grid">
        {days.map((d) => {
          const inMonth = d.getMonth() === viewMonth.getMonth();
          const isToday = isSameDay(d, today);
          const isSelected = selected ? isSameDay(d, selected) : false;
          const disabled = isDisabled(d);
          const classNames = [
            'lf-calendar-day',
            !inMonth && 'lf-calendar-day--muted',
            isToday && 'lf-calendar-day--today',
            isSelected && 'lf-calendar-day--selected',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={toIso(d)}
              type="button"
              className={classNames}
              disabled={disabled}
              // A bare day number ("1") is ambiguous both for a screen
              // reader (which month?) and for this grid specifically (the
              // trailing days from next month can repeat the same number,
              // e.g. Aug 1 and a trailing "Sep 1") — the full date is the
              // accessible name; the visible number stays as-is.
              aria-label={d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              aria-current={isToday ? 'date' : undefined}
              aria-pressed={isSelected}
              onClick={() => handleDayClick(d)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      <div className="lf-calendar-footer">
        <button type="button" className="lf-calendar-link" onClick={onClear}>
          Clear
        </button>
        <button type="button" className="lf-calendar-link" onClick={handleToday}>
          Today
        </button>
      </div>
    </div>
  );
};

export default LogsCalendarPicker;
